import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '../../../components/AppIcon';

gsap?.registerPlugin(ScrollTrigger);

const SolutionSection = () => {
  const [language, setLanguage] = useState('en');
  const [activeChannel, setActiveChannel] = useState('telegram');
  const sectionRef = useRef(null);
  const devicesRef = useRef(null);
  const channelsRef = useRef([]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const ctx = gsap?.context(() => {
      // Animate devices on scroll
      gsap?.fromTo(
        devicesRef?.current,
        { scale: 0.8, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: devicesRef?.current,
            start: 'top 70%',
            end: 'bottom 30%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Animate channel cards
      channelsRef?.current?.forEach((channel, index) => {
        if (channel) {
          gsap?.fromTo(
            channel,
            { y: 30, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
              delay: index * 0.1,
              scrollTrigger: {
                trigger: channel,
                start: 'top 80%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx?.revert();
  }, []);

  const content = {
    en: {
      title: 'One Platform, All Channels',
      subtitle:
        'Seamlessly manage appointments across every communication channel your customers use',
      channels: [
        {
          id: 'telegram',
          name: 'Telegram',
          icon: 'MessageCircle',
          color: 'blue',
          description:
            'Instant booking through Telegram bot with smart conversation flow',
          features: [
            '24/7 automated responses',
            'Multi-language support',
            'Payment integration',
          ],
        },
        {
          id: 'whatsapp',
          name: 'WhatsApp',
          icon: 'MessageSquare',
          color: 'green',
          description:
            'WhatsApp Business API integration for seamless customer communication',
          features: [
            'Rich media messages',
            'Template notifications',
            'Group scheduling',
          ],
        },
        {
          id: 'instagram',
          name: 'Instagram',
          icon: 'Instagram',
          color: 'purple',
          description:
            'Direct booking from Instagram DMs and story interactions',
          features: [
            'Story booking links',
            'DM automation',
            'Visual appointment cards',
          ],
        },
        {
          id: 'web',
          name: 'Web Portal',
          icon: 'Globe',
          color: 'indigo',
          description:
            'Customizable booking widget for your website and social profiles',
          features: [
            'Branded interface',
            'Real-time availability',
            'Mobile responsive',
          ],
        },
      ],
      benefits: [
        {
          icon: 'Zap',
          title: 'AI-Powered Automation',
          description:
            'Smart booking assistant handles complex scheduling scenarios automatically',
        },
        {
          icon: 'Shield',
          title: 'Unified Management',
          description:
            'Single dashboard to manage all bookings regardless of source channel',
        },
        {
          icon: 'TrendingUp',
          title: 'Revenue Optimization',
          description:
            'Dynamic pricing and smart upselling increase average booking value',
        },
      ],
    },
    fa: {
      title: 'یک پلتفرم، همه کانال‌ها',
      subtitle:
        'مدیریت یکپارچه قرارملاقات‌ها در تمام کانال‌های ارتباطی که مشتریانتان استفاده می‌کنند',
      channels: [
        {
          id: 'telegram',
          name: 'تلگرام',
          icon: 'MessageCircle',
          color: 'blue',
          description: 'رزرو فوری از طریق ربات تلگرام با جریان گفتگوی هوشمند',
          features: [
            'پاسخ خودکار 24/7',
            'پشتیبانی چندزبانه',
            'یکپارچگی پرداخت',
          ],
        },
        {
          id: 'whatsapp',
          name: 'واتساپ',
          icon: 'MessageSquare',
          color: 'green',
          description: 'یکپارچگی API واتساپ بیزنس برای ارتباط یکپارچه با مشتری',
          features: [
            'پیام‌های رسانه غنی',
            'اعلان‌های قالبی',
            'برنامه‌ریزی گروهی',
          ],
        },
        {
          id: 'instagram',
          name: 'اینستاگرام',
          icon: 'Instagram',
          color: 'purple',
          description: 'رزرو مستقیم از DM اینستاگرام و تعاملات استوری',
          features: [
            'لینک رزرو استوری',
            'خودکارسازی DM',
            'کارت‌های قرارملاقات بصری',
          ],
        },
        {
          id: 'web',
          name: 'پورتال وب',
          icon: 'Globe',
          color: 'indigo',
          description:
            'ویجت رزرو قابل تنظیم برای وب‌سایت و پروفایل‌های اجتماعی',
          features: ['رابط برندی', 'دسترسی زمان واقعی', 'پاسخگو موبایل'],
        },
      ],
      benefits: [
        {
          icon: 'Zap',
          title: 'خودکارسازی مبتنی بر هوش مصنوعی',
          description:
            'دستیار هوشمند رزرو سناریوهای پیچیده برنامه‌ریزی را خودکار مدیریت می‌کند',
        },
        {
          icon: 'Shield',
          title: 'مدیریت یکپارچه',
          description:
            'داشبورد واحد برای مدیریت همه رزروها صرف‌نظر از کانال منبع',
        },
        {
          icon: 'TrendingUp',
          title: 'بهینه‌سازی درآمد',
          description:
            'قیمت‌گذاری پویا و فروش متقابل هوشمند ارزش متوسط رزرو را افزایش می‌دهد',
        },
      ],
    },
  };

  const currentContent = content?.[language];

  const getChannelColor = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-700',
      green: 'bg-green-50 border-green-200 text-green-700',
      purple: 'bg-purple-50 border-purple-200 text-purple-700',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    };
    return colors?.[color] || colors?.blue;
  };

  const mockDeviceScreens = {
    telegram: {
      messages: [
        {
          sender: 'bot',
          text: 'Hi! I can help you book an appointment. What service do you need?',
        },
        { sender: 'user', text: 'Hair cut please' },
        {
          sender: 'bot',
          text: 'Great! I have these available slots:\n• Today 2:00 PM\n• Tomorrow 10:00 AM\n• Tomorrow 3:00 PM',
        },
      ],
    },
    whatsapp: {
      messages: [
        {
          sender: 'bot',
          text: '✅ Appointment confirmed!\n📅 Nov 15, 2:00 PM\n💇‍♀️ Hair Styling with Sarah\n📍 Downtown Salon',
        },
        { sender: 'user', text: 'Perfect! Can I reschedule if needed?' },
        {
          sender: 'bot',
          text: 'Absolutely! You can reschedule up to 2 hours before your appointment.',
        },
      ],
    },
    instagram: {
      messages: [
        { sender: 'bot', text: 'Thanks for your DM! 💜' },
        {
          sender: 'bot',
          text: "I see you're interested in our spa services. Here are today's available slots:",
        },
        { sender: 'user', text: 'The 4 PM slot looks good!' },
      ],
    },
    web: {
      interface: true,
    },
  };

  return (
    <section ref={sectionRef} className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 ${language === 'fa' ? 'rtl' : 'ltr'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {currentContent?.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {currentContent?.subtitle}
          </p>
        </div>

        {/* Channel Selection */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {currentContent?.channels?.map((channel, index) => (
            <button
              key={channel?.id}
              ref={(el) => (channelsRef.current[index] = el)}
              onClick={() => setActiveChannel(channel?.id)}
              className={`px-6 py-3 rounded-lg border-2 transition-all duration-300 ${
                activeChannel === channel?.id
                  ? getChannelColor(channel?.color)
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}>
              <div className="flex items-center space-x-2">
                <Icon name={channel?.icon} size={20} />
                <span className="font-medium">{channel?.name}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Device Mockups */}
        <div ref={devicesRef} className="max-w-6xl mx-auto mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Device Visual */}
            <div className="relative">
              {activeChannel === 'web' ? (
                // Web Interface Mockup
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                  <div className="bg-gray-100 px-4 py-3 flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <div className="flex-1 text-center text-sm text-gray-600">
                      yoursite.com/book
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-4">
                      Book Your Appointment
                    </h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-2">
                        {['Hair Cut', 'Styling', 'Color']?.map((service, i) => (
                          <div
                            key={i}
                            className="p-3 bg-blue-50 rounded-lg text-center text-sm">
                            {service}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {['9:00', '11:00', '2:00', '4:00']?.map((time, i) => (
                          <div
                            key={i}
                            className={`p-2 rounded text-center text-sm ${
                              i === 2 ? 'bg-blue-600 text-white' : 'bg-gray-100'
                            }`}>
                            {time}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Mobile Chat Interface
                <div className="bg-black rounded-3xl p-2 shadow-2xl max-w-sm mx-auto">
                  <div className="bg-white rounded-2xl overflow-hidden">
                    <div className="bg-gray-100 px-4 py-3 flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <Icon name="Bot" size={16} color="white" />
                      </div>
                      <div>
                        <div className="font-medium text-sm">Slotyfi Bot</div>
                        <div className="text-xs text-gray-500">Online</div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3 h-64 overflow-y-auto">
                      {mockDeviceScreens?.[activeChannel]?.messages?.map(
                        (message, index) => (
                          <div
                            key={index}
                            className={`flex ${
                              message?.sender === 'user'
                                ? 'justify-end'
                                : 'justify-start'
                            }`}>
                            <div
                              className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                                message?.sender === 'user'
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                              {message?.text}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Channel Details */}
            <div
              className={`space-y-6 ${
                language === 'fa' ? 'text-right' : 'text-left'
              }`}>
              {currentContent?.channels?.find(
                (c) => c?.id === activeChannel
              ) && (
                <>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {
                        currentContent?.channels?.find(
                          (c) => c?.id === activeChannel
                        )?.name
                      }
                    </h3>
                    <p className="text-lg text-gray-600">
                      {
                        currentContent?.channels?.find(
                          (c) => c?.id === activeChannel
                        )?.description
                      }
                    </p>
                  </div>
                  <div className="space-y-3">
                    {currentContent?.channels
                      ?.find((c) => c?.id === activeChannel)
                      ?.features?.map((feature, index) => (
                        <div key={index} className="flex items-center">
                          <Icon
                            name="Check"
                            size={20}
                            color="#059669"
                            className={`${language === 'fa' ? 'ml-3' : 'mr-3'}`}
                          />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {currentContent?.benefits?.map((benefit, index) => (
            <div key={index} className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name={benefit?.icon} size={32} color="#2563EB" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {benefit?.title}
              </h3>
              <p className="text-gray-600">{benefit?.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
